import React, { useEffect, useState } from 'react';
import { Blurhash } from 'react-blurhash';
import { FaExpand, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { Image as ImageIcon, ArrowLeft } from 'lucide-react';
import { AlbumsService, Album, Image } from '../../services/albums.service';
import { Helmet } from 'react-helmet';

// --- Components ---

const ImageCard: React.FC<{ image: Image; onClick: () => void }> = ({ image, onClick }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const img = new window.Image();
    img.onload = () => {
      setLoaded(true);
    };
    img.src = image.thumb_url;
  }, [image.thumb_url]);

  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
      className="relative aspect-square cursor-pointer group overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300"
      onClick={onClick}
    >
      <div className={`absolute inset-0 transition-opacity duration-500 ${loaded ? 'opacity-0' : 'opacity-100'}`}>
        {image.blurhash && (
          <Blurhash
            hash={image.blurhash}
            width="100%"
            height="100%"
            resolutionX={32}
            resolutionY={32}
            punch={1}
          />
        )}
      </div>
      <img
        src={image.thumb_url}
        alt=""
        className={`w-full h-full object-cover transition-all duration-500 ${loaded ? 'opacity-100' : 'opacity-0'} group-hover:scale-110`}
        loading="lazy"
      />
      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
        <FaExpand className="text-white opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all duration-300" size={28} />
      </div>
    </motion.div>
  );
};

const Lightbox: React.FC<{
  image: Image;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
}> = ({ image, onClose, onNext, onPrev }) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft') onPrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onNext, onPrev]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-95" onClick={onClose}>
      <button className="absolute top-4 right-4 text-white p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors" onClick={onClose}>
        <FaTimes size={32} />
      </button>
      <button className="absolute left-4 top-1/2 -translate-y-1/2 text-white p-4 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors" onClick={(e) => { e.stopPropagation(); onPrev(); }}>
        <FaChevronLeft size={32} />
      </button>
      <div className="max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <img src={image.original_url} alt="" className="max-w-full max-h-[90vh] object-contain" />
      </div>
      <button className="absolute right-4 top-1/2 -translate-y-1/2 text-white p-4 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors" onClick={(e) => { e.stopPropagation(); onNext(); }}>
        <FaChevronRight size={32} />
      </button>
    </div>
  );
};


const Galerie: React.FC = () => {
  const [albums, setAlbums] = useState<Album[]>([]);
  const [selectedAlbum, setSelectedAlbum] = useState<Album | null>(null);
  const [lightboxImageIndex, setLightboxImageIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAlbums();
  }, []);

  const loadAlbums = async () => {
    try {
      const res = await AlbumsService.findAll(1, 100);
      setAlbums(res.data);
    } catch (e) {
      console.error("Failed to load albums", e);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenLightbox = (index: number) => {
    setLightboxImageIndex(index);
    document.body.style.overflow = 'hidden';
  };

  const handleCloseLightbox = () => {
    setLightboxImageIndex(null);
    document.body.style.overflow = 'unset';
  };

  const handleNextImage = () => {
    if (selectedAlbum && lightboxImageIndex !== null) {
      setLightboxImageIndex((prev) => (prev! + 1) % selectedAlbum.images.length);
    }
  };

  const handlePrevImage = () => {
    if (selectedAlbum && lightboxImageIndex !== null) {
      setLightboxImageIndex((prev) => (prev! - 1 + selectedAlbum.images.length) % selectedAlbum.images.length);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number]
      }
    }
  };

  return (
    <div className="bg-white min-h-screen pt-20">
      <Helmet>
        <title>Galerie Photos - SAPI</title>
      </Helmet>

      {/* Hero Section */}
      <div className="bg-white pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm font-semibold text-primary uppercase tracking-wider mb-2">GALERIE</p>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Galerie Photos
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Découvrez nos moments marquants et nos réalisations à travers nos albums photos
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-20">
        {loading ? (
          <div className="py-20 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
            <p className="text-gray-600">Chargement des albums...</p>
          </div>
        ) : (
          <>
            {!selectedAlbum ? (
              /* Album Grid */
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                {albums.map(album => (
                  <motion.div
                    key={album.id}
                    variants={cardVariants}
                    whileHover={{ y: -8 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white shadow-xl hover:shadow-2xl overflow-hidden cursor-pointer group relative"
                    onClick={() => setSelectedAlbum(album)}
                  >
                    <div className="h-80 bg-gray-100 relative overflow-hidden">
                      {album.images.length > 0 ? (
                        <img
                          src={album.images[0].medium_url}
                          alt={album.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300">
                          <ImageIcon className="w-20 h-20" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>

                      {/* Title with glassmorphism */}
                      <div className="absolute bottom-0 left-0 right-0 p-6 backdrop-blur-md bg-white/30 border-t border-white/20">
                        <h3 className="text-xl font-bold text-white mb-1 group-hover:text-secondary transition-colors drop-shadow-lg">
                          {album.title}
                        </h3>
                        <span className="text-sm text-white/90 drop-shadow-md">{album.images.length} photos</span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              /* Selected Album View */
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
              >
                <button
                  onClick={() => setSelectedAlbum(null)}
                  className="mb-8 inline-flex items-center text-primary hover:text-secondary font-medium transition-colors text-lg group"
                >
                  <ArrowLeft className="mr-2 w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                  Retour aux albums
                </button>

                <div className="mb-10">
                  <h2 className="text-4xl font-bold text-gray-900 mb-3">{selectedAlbum.title}</h2>
                  {selectedAlbum.description && <p className="text-gray-600 text-lg max-w-3xl">{selectedAlbum.description}</p>}
                  <p className="text-sm text-gray-500 mt-2">{selectedAlbum.images.length} photos</p>
                </div>

                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
                >
                  {selectedAlbum.images.map((image, index) => (
                    <motion.div key={image.id} variants={cardVariants}>
                      <ImageCard
                        image={image}
                        onClick={() => handleOpenLightbox(index)}
                      />
                    </motion.div>
                  ))}
                </motion.div>
              </motion.div>
            )}
          </>
        )}
      </div>

      {/* Lightbox Overlay */}
      {selectedAlbum && lightboxImageIndex !== null && (
        <Lightbox
          image={selectedAlbum.images[lightboxImageIndex]}
          onClose={handleCloseLightbox}
          onNext={handleNextImage}
          onPrev={handlePrevImage}
        />
      )}
    </div>
  );
};

export default Galerie;
